import { useMemo, useState } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";

import { SERPSnapshotEmptyState } from "app/pages/keyword-research/keyword-analysis/pages/serp-snapshot/components/empty-state/SERPSnapshotEmptyState";
import { tableColumns } from "app/pages/keyword-research/keyword-analysis/pages/serp-snapshot/SERPSnapshotColumns";
import { SERPSnapshotTableTop } from "app/pages/keyword-research/keyword-analysis/pages/serp-snapshot/SERPSnapshotTableTop";
import { SERPSnapshotPageTableWrapper } from "app/pages/keyword-research/keyword-analysis/pages/serp-snapshot/StyledComponents";
import type {
    IRecord,
    IRecordWithType,
    ISERPTableServerResponse,
    SERPRecords,
} from "app/pages/keyword-research/keyword-analysis/pages/serp-snapshot/types";
import { ESERPMetaType } from "app/pages/keyword-research/keyword-analysis/pages/serp-snapshot/types";
import { swSettings } from "app/common/services/swSettings";
import SWReactTableWrapper from "app/components/React/Table/SWReactTableWrapper";
import { proNavigator } from "single-spa/react-app/ProNavigator";
import { allTrackers } from "app/services/track/track";

import { SERPSnapshotEnrichedRowComponent } from "./SERPSnapshotEnrichedRowComponent";

const SERVER_API = "/api/SerpSnapshot/Table";
const EXCEL_URL = `${SERVER_API}/Excel`;
const DEFAULT_SORT = "position";

export const SERPSnapshotPage = () => {
    const {
        keyword,
        country,
        duration,
        webSource,
        ExcludeUrls,
        IncludeUrls,
        sort = DEFAULT_SORT,
        asc = false,
    } = useCurrentStateAndParams().params;
    const { to, from } = proNavigator.getApiParams();
    const [serpRecords, setSerpRecords] = useState<SERPRecords>({});
    const [noSerpRecords, setNoSerpRecords] = useState(false);
    const [lastScrapeDate, setLastScrapeDate] = useState("");
    const downloadExcelPermitted = swSettings.current.resources.IsExcelAllowed;

    const apiParams = {
        keys: keyword,
        to,
        from,
        webSource,
        country,
        sort,
        asc,
        ExcludeUrls,
        IncludeUrls,
    };

    if (ExcludeUrls) {
        apiParams["ExcludeUrls"] = ExcludeUrls;
    } else {
        delete apiParams["ExcludeUrls"];
    }
    if (IncludeUrls) {
        apiParams["IncludeUrls"] = IncludeUrls;
    } else {
        delete apiParams["IncludeUrls"];
    }

    const transformData = (data: ISERPTableServerResponse) => {
        if (!data?.records) {
            return data;
        }
        if (Object.keys(data.serpRecords).length) {
            setSerpRecords(data.serpRecords);
        } else if (data.records.length) {
            setNoSerpRecords(true);
        }
        setLastScrapeDate(data.lastScrapeDate);

        // for mark the organic serps
        // to have indication where to put the serp features in the table
        const transformedOrganicRecords: IRecordWithType | IRecord[] = data.records.map((result: IRecord) => {
            if (result.serpFeature === (ESERPMetaType.ORGANIC as string)) {
                return {
                    ...result,
                    type: ESERPMetaType.ORGANIC,
                };
            }
            return result;
        });

        return {
            records: transformedOrganicRecords,
            totalRecords: transformedOrganicRecords.length,
        };
    };

    const onSort = ({ field, sortDirection }) => {
        proNavigator.applyUpdateParams({
            sort: `${field}`,
            asc: `${sortDirection === "asc"}`,
        });
    };

    const columns = useMemo(
        () =>
            tableColumns.getColumnsConfig({
                sortedColumn: DEFAULT_SORT,
                sortDirection: asc ? "asc" : "desc",
                currentMonth: duration.split("-")[0],
            }),
        [],
    );

    const isKeywordGroup = keyword.startsWith("*");

    if (isKeywordGroup) {
        return <SERPSnapshotEmptyState />;
    }

    return (
        <SERPSnapshotPageTableWrapper>
            <SWReactTableWrapper
                serverApi={SERVER_API}
                tableColumns={columns}
                initialFilters={{ ...apiParams }}
                onSort={onSort}
                transformData={transformData}
                totalRecordsField={"totalRecords"}
                paginationProps={{
                    showPagination: false,
                }}
                tableOptions={{
                    get EnrichedRowComponent() {
                        return function (props) {
                            return <SERPSnapshotEnrichedRowComponent {...props} />;
                        };
                    },
                    get enrichedRowComponentAppendTo() {
                        return ".serp-snapshot-table";
                    },
                    get enrichedRowComponentHeight() {
                        return 580;
                    },
                    shouldApplyEnrichedRowHeightToCell: false,
                    shouldEnrichRow: (props, index, e) => {
                        const openEnrich = !!e?.currentTarget?.classList?.contains("enrich");
                        if (openEnrich) {
                            allTrackers.dispatchTrackEvent("Open", "Click", "SERP Snapshot/Expand");
                        }
                        return openEnrich;
                    },
                    onEnrichedRowClick: () => {},
                    customTableClass: "serp-snapshot-table",
                }}
            >
                {(topComponentProps) => (
                    <SERPSnapshotTableTop
                        {...topComponentProps}
                        serpRecords={serpRecords}
                        noSerpRecords={noSerpRecords}
                        lastScrapeDate={lastScrapeDate}
                        excelAllowed={downloadExcelPermitted}
                        excelDownloadUrl={EXCEL_URL}
                    />
                )}
            </SWReactTableWrapper>
        </SERPSnapshotPageTableWrapper>
    );
};
